import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { productService } from "../../services/api";
import { Product } from "../../types";
import LoadingSpinner from "../../components/LoadingSpinner";

const API_URL = process.env.REACT_APP_API_URL || "";

const ProductDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [product, setProduct] = useState<Product | null>(null);
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        loadProduct();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadProduct = async () => {
        if (!id) return;
        try {
            setIsLoading(true);
            const data = await productService.getById(parseInt(id));
            setProduct(data);
        } catch (err) {
            setError("Produit non trouve");
        } finally {
            setIsLoading(false);
        }
    };

    const unitLabels: Record<string, string> = {
        gramme: "g",
        unite: "u",
        kg: "kg",
    };

    const formatPriceWithQuantity = (
        price: number,
        quantity: number,
        unit: string,
    ) => {
        const qty = quantity > 0 ? quantity : 1;
        return `${price.toFixed(2)} EUR / ${qty}${unitLabels[unit] || unit}`;
    };

    const getDisplayPrice = () => {
        const activePrices = product?.prices?.filter((p) => p.is_active) || [];
        if (activePrices.length > 0) {
            const firstPrice = activePrices[0];
            return formatPriceWithQuantity(
                firstPrice.price,
                firstPrice.quantity,
                firstPrice.price_unit,
            );
        }
        return `${product?.price.toFixed(2)} EUR / ${unitLabels[product?.price_unit || "unite"]}`;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" text="Chargement du produit..." />
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-white mb-4">
                        Produit non trouve
                    </h1>
                    <Link to="/products" className="btn-primary">
                        Retour aux produits
                    </Link>
                </div>
            </div>
        );
    }

    const currentMedia = product.media?.[selectedMediaIndex];
    const mediaUrl = currentMedia
        ? `${API_URL}/${currentMedia.media_path}`
        : "/images/placeholder.jpg";

    return (
        <div className="min-h-screen py-8 px-4 relative">
            {/* Background image */}
            <div className="fixed inset-0 overflow-hidden">
                <div style={{
                    position: "absolute", inset: 0,
                    backgroundImage: "url('/bg.png')",
                    backgroundSize: "cover", backgroundPosition: "center",
                }}></div>
            </div>
            {/* Background overlay with opacity */}
            <div className="fixed inset-0 bg-primary-dark/85"></div>

            <div className="max-w-6xl mx-auto relative z-10">
                {/* Breadcrumb */}
                <nav className="mb-6">
                    <Link
                        to="/products"
                        className="text-accent hover:text-accent-light transition-colors"
                    >
                        &larr; Retour aux produits
                    </Link>
                </nav>

                <div className="grid md:grid-cols-2 gap-8">
                    {/* Media Gallery */}
                    <div className="space-y-4">
                        {/* Main Media */}
                        <div className="card overflow-hidden aspect-square">
                            {currentMedia?.media_type === "video" ? (
                                <video
                                    src={mediaUrl}
                                    controls
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <img
                                    src={mediaUrl}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src =
                                            "/images/placeholder.jpg";
                                    }}
                                />
                            )}
                        </div>

                        {/* Thumbnails */}
                        {product.media && product.media.length > 1 && (
                            <div className="flex space-x-2 overflow-x-auto pb-2">
                                {product.media.map((media, index) => (
                                    <button
                                        key={media.id}
                                        onClick={() =>
                                            setSelectedMediaIndex(index)
                                        }
                                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                                            index === selectedMediaIndex
                                                ? "border-accent"
                                                : "border-transparent opacity-60 hover:opacity-100"
                                        }`}
                                    >
                                        {media.media_type === "video" ? (
                                            <div className="w-full h-full bg-primary-light flex items-center justify-center">
                                                <svg
                                                    className="w-8 h-8 text-white"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                            </div>
                                        ) : (
                                            <img
                                                src={`${API_URL}/${media.media_path}`}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Product Info */}
                    <div className="space-y-6">
                        <div>
                            {product.category && (
                                <span className="inline-block bg-accent/20 text-accent text-sm px-3 py-1 rounded-full mb-3">
                                    {product.category.name}
                                </span>
                            )}
                            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                                {product.name}
                            </h1>

                            {/* Prix */}
                            {product.prices &&
                            product.prices.filter((p) => p.is_active).length >
                                0 ? (
                                <div className="space-y-2">
                                    {product.prices
                                        .filter((p) => p.is_active)
                                        .map((price, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-3"
                                            >
                                                <span className="text-accent font-bold text-xl">
                                                    {formatPriceWithQuantity(
                                                        price.price,
                                                        price.quantity,
                                                        price.price_unit,
                                                    )}
                                                </span>
                                                {price.label && (
                                                    <span className="text-gray-400 text-sm">
                                                        ({price.label})
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                <p className="text-3xl font-bold text-accent">
                                    {getDisplayPrice()}
                                </p>
                            )}
                        </div>

                        {/* Availability */}
                        <div className="flex items-center space-x-2">
                            <span
                                className={`w-3 h-3 rounded-full ${product.is_available ? "bg-green-500" : "bg-red-500"}`}
                            ></span>
                            <span
                                className={
                                    product.is_available
                                        ? "text-green-400"
                                        : "text-red-400"
                                }
                            >
                                {product.is_available
                                    ? "Disponible"
                                    : "Indisponible"}
                            </span>
                        </div>

                        {/* Description */}
                        {product.description && (
                            <div className="card p-6">
                                <h2 className="text-lg font-semibold text-white mb-3">
                                    Description
                                </h2>
                                <p className="text-gray-300 whitespace-pre-line">
                                    {product.description}
                                </p>
                            </div>
                        )}

                        {/* Contact CTA */}
                        <div className="card p-6 bg-accent/10 border-accent/30">
                            <h3 className="text-lg font-semibold text-white mb-2">
                                Interesse ?
                            </h3>
                            <p className="text-gray-300 mb-4">
                                Contactez-nous pour passer commande
                            </p>
                            <Link
                                to="/contact"
                                className="btn-primary inline-block"
                            >
                                Nous contacter
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;
